/**
 * 시즌별 그룹(매치업) 카드 목록을 **탭에 남겨 둔다.**
 *
 * 시즌 목록은 이미 세션 저장소에 남겨서 화면을 열면 곧바로 뜨는데, 그다음 화면인
 * **그룹 카드는 매번 새로 받았다.** 그래서 시즌을 고르면 카드가 뜨기까지 아무것도
 * 없는 화면을 보게 된다(NAS 왕복이라 눈에 띈다).
 *
 * 저장된 값이 있으면 **먼저 그리고**, 뒤에서 새로 받아 갱신한다. 카드에 적히는 승/패는
 * 점령전이 새로 수집될 때만 바뀌므로, 잠깐 옛 숫자가 보이는 것은 문제가 되지 않는다.
 *
 * 저장 한도가 넉넉하지 않으므로(기록은 시즌당 몇 MB라 아예 저장하지 않는다) 카드 목록만
 * 담는다 — 한 시즌이 1KB 남짓이다.
 *
 * 시즌 목록(`siege-seasons-v2`)도 같은 이유로 브라우저에 남긴다 — 그건 `SiegeBattleList`
 * 가 직접 들고 있다.
 */

const PREFIX = 'siege-groupcards-v1:';
/**
 * 이 시간이 지나면 저장된 값을 쓰지 않는다.
 *
 * 한 주면 "열면 바로 보인다"가 늘 성립한다 — 어차피 뒤에서 새로 받아 갱신하므로 길게
 * 두어도 틀린 값이 남지 않는다. 저장은 **`localStorage`** 에 한다. 탭 단위
 * (`sessionStorage`)로 두면 새 탭에서 열 때마다 기다리게 된다(시즌 목록도 같은 이유로
 * 옮겼다).
 */
const TTL_MS = 7 * 24 * 60 * 60 * 1000;

/** 저장된 그룹 목록. 없거나 오래됐으면 null */
export function readGroups(season: number | null | undefined): any[] | null {
    if (!season) return null;
    try {
        const raw = localStorage.getItem(PREFIX + season);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.groups)) return null;
        if (Date.now() - saved.at > TTL_MS) return null;
        return saved.groups;
    } catch {
        return null;   // 저장소를 못 읽어도 그냥 받아온다
    }
}

export function writeGroups(season: number | null | undefined, groups: any[]): void {
    if (!season || !Array.isArray(groups)) return;
    try {
        localStorage.setItem(PREFIX + season, JSON.stringify({ at: Date.now(), groups }));
    } catch {
        /* 한도를 넘기면 넘긴다 — 다음에 다시 받으면 된다 */
    }
}

/**
 * 전투기록 탭의 매치업 목록(`/api/siege-battles/groups`)도 같이 남긴다.
 *
 * 시즌·그룹 화면은 두 곳에서 그려진다. 전투기록 탭은 **모든 시즌의 그룹을 한 번에** 받아
 * (`logType` 별로) 화면에서 시즌으로 걸러 쓰고, 길드원 탭 등은 시즌별로 받는다. 전투기록
 * 쪽은 모듈 변수에만 담아 뒀어서 **새로고침하면 사라졌다** — 그래서 시즌을 고르면 카드가
 * 뜨기까지 1초쯤 빈 화면이었다(실측).
 */
const BATTLE_PREFIX = 'siege-battlegroups-v1:';

export function readBattleGroups(logType: number): any[] | null {
    try {
        const raw = localStorage.getItem(BATTLE_PREFIX + logType);
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || !Array.isArray(saved.groups)) return null;
        if (Date.now() - saved.at > TTL_MS) return null;
        return saved.groups;
    } catch {
        return null;
    }
}

export function writeBattleGroups(logType: number, groups: any[]): void {
    if (!Array.isArray(groups)) return;
    try {
        localStorage.setItem(BATTLE_PREFIX + logType, JSON.stringify({ at: Date.now(), groups }));
    } catch {
        /* 한도를 넘기면 넘긴다 */
    }
}

/** 서버에서 받아 저장까지 한다 */
export async function fetchGroups(season: number): Promise<any[]> {
    const res = await fetch(`/api/siege-groups/groups?season=${season}`);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    const groups = data.groups || [];
    writeGroups(season, groups);
    return groups;
}
